import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/modal-shell";
import { useUser } from "@/hooks/useUser";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import {
  DRIVESHARE_REDIRECT_URL,
  buildDriveShareDeepLink,
  getDriveShareRedirectUrl,
  logDriveShareOutbound,
} from "@/lib/driveshareRedirect";
import driveShareLogo from "@assets/driveshare-green-logo_1756734944745-De_wRc_a_1781716449728.png";

const FLAG_KEY = "feature_driveshare_login_redirect" as const;
// Once-per-sign-in-session marker. Cleared automatically when the tab session
// ends, so a fresh sign-in can nudge again without nagging on every render or
// navigation within the same session.
const SESSION_SHOWN_KEY = "bac.driveshareLoginNudgeShown";

// DriveShare's OWN brand green for the on-brand cross-brand CTA — deliberately
// NOT BookAClassic's brand green, so it is exempt from the BAC brand-color
// guard.
const DRIVESHARE_CTA_GREEN = "bg-[#0f5132] hover:bg-[#0c4429]"; // brand-color-allow: DriveShare brand green on the cross-brand login nudge CTA

function markSessionShown() {
  try {
    if (typeof window !== "undefined") window.sessionStorage.setItem(SESSION_SHOWN_KEY, "1");
  } catch { /* noop */ }
}

function alreadyShownThisSession(): boolean {
  try {
    return typeof window !== "undefined" && !!window.sessionStorage.getItem(SESSION_SHOWN_KEY);
  } catch {
    return false;
  }
}

/**
 * Dismissible post-login nudge that points DriveShare-origin users at
 * driveshare.com (Task #4640). The destination is resolved at open time via
 * the shared `getDriveShareRedirectUrl()` resolver (platform_config override →
 * `DRIVESHARE_REDIRECT_URL` fallback) rather than being hard-coded.
 *
 * Login is never blocked: dismissing (X / "Continue on BookAClassic") leaves
 * the user fully signed in.
 */
function DriveShareLoginModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { profile, session, signOut } = useUser();
  const userId = profile?.id ? String(profile.id) : null;
  const [url, setUrl] = useState<string>(DRIVESHARE_REDIRECT_URL);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getDriveShareRedirectUrl().then((u) => { if (!cancelled) setUrl(u); });
    return () => { cancelled = true; };
  }, [open]);

  const href = useMemo(
    () => buildDriveShareDeepLink(url, {
      utm_source: "bookaclassic",
      utm_medium: "login_modal",
      utm_campaign: "driveshare_login_redirect",
    }),
    [url],
  );

  // "Continue to DriveShare" must NOT leave the user signed in to BookAClassic.
  // We intercept the click, preserve the outbound logging, sign the user out of
  // their BAC session (when one exists), and only then navigate to the resolved
  // driveshare.com destination — so they land there without an active BAC
  // session. Logging happens BEFORE sign-out so we still capture the user id,
  // and the redirect always proceeds even if sign-out throws.
  const onContinue = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      logDriveShareOutbound(FLAG_KEY, userId, "login_modal");
      try {
        if (session) await signOut();
      } catch { /* proceed to redirect regardless of sign-out outcome */ }
      if (typeof window !== "undefined") window.location.assign(href);
    },
    [userId, session, signOut, href],
  );

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      size="md"
      showClose
      hideHeaderDivider
      data-testid="driveshare-login-modal"
    >
      <div className="flex flex-col items-center text-center">
        <img
          src={driveShareLogo}
          alt={t("driveshareLogin.logo_alt")}
          className="h-9 w-auto"
        />
        <h2 className="mt-5 text-lg font-semibold text-gray-900">
          {t("driveshareLogin.title")}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">
          {t("driveshareLogin.body")}
        </p>

        <a
          href={href}
          onClick={onContinue}
          rel="noopener"
          className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg ${DRIVESHARE_CTA_GREEN} px-4 py-2.5 text-sm font-semibold text-white`}
          data-testid="driveshare-login-modal-cta"
        >
          {t("driveshareLogin.continue_cta")}
          <ArrowRight className="h-4 w-4 shrink-0" />
        </a>

        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer mt-3 text-sm font-medium text-gray-600 underline-offset-2 hover:text-gray-900 hover:underline"
          data-testid="driveshare-login-modal-dismiss"
        >
          {t("driveshareLogin.stay_cta")}
        </button>

        <p className="mt-4 text-xs text-gray-500">
          {t("driveshareLogin.leave_notice")}
        </p>
      </div>
    </ModalShell>
  );
}

/**
 * Global watcher mounted alongside the other auth watchers. Opens the
 * DriveShare login nudge once per sign-in session when ALL of the following
 * hold:
 *   • the `feature_driveshare_login_redirect` flag is ON,
 *   • the user is signed in with a materialised profile,
 *   • the server-derived `isDriveShareOrigin` boolean is true, and
 *   • we haven't already shown it this session.
 *
 * IMPORTANT: eligibility deliberately does NOT depend on `profile.is_complete`.
 * Migrated DriveShare accounts usually have an INCOMPLETE BookAClassic profile
 * (e.g. no accepted BAC terms), so gating on completeness used to route them
 * into the blocking ProfileCompletionDialog and the nudge never fired. A
 * DriveShare user should be nudged to LEAVE before being asked to finish a BAC
 * profile, so this nudge takes precedence over the completion dialog.
 *
 * To avoid stacking two modals, the watcher reports its "active" state up via
 * `onActiveChange`; the caller suppresses the ProfileCompletionDialog while the
 * nudge owns the screen, then lets it render once the user dismisses the nudge.
 *
 * Flag-OFF is fully inert — no extra fetches, no modal — and a genuine BAC user
 * (isDriveShareOrigin !== true) never triggers it even with the flag ON.
 */
export function DriveShareLoginNudgeWatcher({
  onActiveChange,
}: { onActiveChange?: (active: boolean) => void } = {}) {
  const enabled = useFeatureFlag(FLAG_KEY);
  const { user, profile } = useUser();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isDriveShareOrigin = profile?.isDriveShareOrigin === true;
  const eligible = enabled && !!user && !!profile && isDriveShareOrigin;

  useEffect(() => {
    if (!eligible) return;
    if (alreadyShownThisSession()) return;
    markSessionShown();
    setOpen(true);
  }, [eligible]);

  // "active" = the nudge currently owns the screen, so the caller must defer the
  // ProfileCompletionDialog (no stacked modals). It is true from the moment we
  // know the user is an eligible DriveShare user until they dismiss it, and
  // false once dismissed or once it has already been shown-and-closed earlier
  // this session (so a remount can't strand the completion dialog).
  const active = eligible && (open || (!dismissed && !alreadyShownThisSession()));

  useEffect(() => {
    onActiveChange?.(active);
  }, [active, onActiveChange]);
  useEffect(() => () => { onActiveChange?.(false); }, [onActiveChange]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setDismissed(true);
  }, []);

  if (!enabled || !eligible) return null;
  return <DriveShareLoginModal open={open} onClose={handleClose} />;
}

export default DriveShareLoginModal;
